import { useCallback, useEffect, useState, RefObject } from 'react';
import hasAudioTrack from '@/utils/file/hasAudioTrack';
import {
  AUDIO_DETECTION_WINDOW_MS,
  AUDIO_DETECTION_POLL_INTERVAL_MS,
} from '@/consts/video';

type UseVideoAudioDetectionOptions = {
  videoRef: RefObject<HTMLVideoElement>;
  isDetectionEnabled: boolean;
  videoSourceId?: string;
};

const useVideoAudioDetection = ({
  videoRef,
  isDetectionEnabled,
  videoSourceId,
}: UseVideoAudioDetectionOptions) => {
  const [hasAudio, setHasAudio] = useState(false);

  useEffect(() => {
    setHasAudio(false);
  }, [videoSourceId]);

  useEffect(() => {
    if (!isDetectionEnabled) return;

    const detectAudio = () => {
      const video = videoRef.current;
      if (video && hasAudioTrack(video)) {
        setHasAudio(true);
        clearInterval(pollIntervalId);
        clearTimeout(stopPollingTimeoutId);
      }
    };

    const pollIntervalId = setInterval(
      detectAudio,
      AUDIO_DETECTION_POLL_INTERVAL_MS
    );
    const stopPollingTimeoutId = setTimeout(
      () => clearInterval(pollIntervalId),
      AUDIO_DETECTION_WINDOW_MS
    );
    detectAudio();

    return () => {
      clearInterval(pollIntervalId);
      clearTimeout(stopPollingTimeoutId);
    };
  }, [videoRef, isDetectionEnabled, videoSourceId]);

  const handleVideoLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video && hasAudioTrack(video)) setHasAudio(true);
  }, [videoRef]);

  return { hasAudio, handleVideoLoadedMetadata };
};

export default useVideoAudioDetection;
